import { create } from 'zustand'
import { AuthSession } from '@/core/domain/entities/User'
import { configService } from '@/core/services/ConfigService'

export interface SessionState {
  session: AuthSession | null

  // Actions
  setSession: (session: AuthSession | null) => void
  clearSession: () => void
  getSession: () => AuthSession | null
  isSessionExpired: () => boolean
}

const SESSION_STORAGE_KEY = configService.auth.sessionKey

const isLocalStorageAvailable = (): boolean => {
  return typeof localStorage !== 'undefined' && localStorage !== null
}

const getStoredSession = (): AuthSession | null => {
  if (!isLocalStorageAvailable()) return null

  try {
    const sessionData = localStorage.getItem(SESSION_STORAGE_KEY)
    if (!sessionData) return null

    const session = JSON.parse(sessionData) as AuthSession

    // Check if session is expired
    if (new Date() > new Date(session.expiresAt)) {
      clearStoredSession()
      return null
    }

    return session
  } catch (error) {
    console.error('Failed to load session from localStorage:', error)
    return null
  }
}

const setStoredSession = (session: AuthSession | null): void => {
  if (!isLocalStorageAvailable()) return

  try {
    if (session) {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session))
    } else {
      localStorage.removeItem(SESSION_STORAGE_KEY)
    }
  } catch (error) {
    console.error('Failed to store session to localStorage:', error)
  }
}

const clearStoredSession = (): void => {
  if (!isLocalStorageAvailable()) return
  localStorage.removeItem(SESSION_STORAGE_KEY)
}

export const useSessionStore = create<SessionState>((set, get) => ({
  session: getStoredSession(),

  setSession: (session: AuthSession | null) => {
    setStoredSession(session)
    set({ session })
  },

  clearSession: () => {
    clearStoredSession()
    set({ session: null })
  },

  getSession: () => {
    const state = get()
    if (state.session && get().isSessionExpired()) {
      get().clearSession()
      return null
    }
    return state.session
  },

  isSessionExpired: () => {
    const session = get().session
    if (!session) return true
    return new Date() > new Date(session.expiresAt)
  },
}))